import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useMount } from 'react-use';
import { Box, Divider, Typography } from '@mui/material';
import classNames from 'classnames';
import { getUserSettings, getWindow } from '@surfline/web-common';

import Disclosure from 'components/Icons/Disclosure';
import StarCircle from 'components/Icons/StarCircle';
import ForecastDayContent from 'components/ForecastDayContent';
import ObservedDataToggle from 'components/ObservedDataToggle';
import { useMaxWidthTablet } from 'hooks/useMediaQueries';
import { getSpotReportNotes } from 'selectors/spot';
import { useAppSelector } from 'stores/hooks';
import type { ConditionRating } from 'types/conditionRating';
import type { ConsistencyTimestep } from 'types/consistency';
import type { SpotReportNotes } from 'types/spot';
import type { SubregionDaySummaryData } from 'types/subregion';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { SurfObservation, SurfTimestep } from 'types/surf';
import type { SwellsTimestep } from 'types/swell';
import type { TideData } from 'types/tide';
import type { Units } from 'types/units';
import type { WeatherDetail } from 'types/weather';
import type { WindTimestep } from 'types/wind';
import type { WaveEnergyGraphData } from 'types/waveEnergy';
import { formatTimestampInTimezone } from 'utils/time';

import TableHeaderHourlyToggle from '../TableHeaderHourlyToggle';
import TimeTableContainer from '../TimeTableContainer';
import ForecastTableRows from '../ForecastTableRows';

import styles from './TableDayContainer.module.scss';

interface Props {
  consistencyDay: Array<ConsistencyTimestep>;
  dayIndex: number;
  daySummary: SubregionDaySummaryData;
  hideProbability: boolean;
  isExpanded: boolean;
  isHourlyExpanded?: boolean;
  isHistoricView: boolean;
  isLoading: boolean;
  observations?: Array<SurfObservation> | undefined;
  onExpandCollapse: (dayIndex: number) => void;
  onToggleHourly?: (dayIndex: number) => void;
  ratingDay: Array<ConditionRating>;
  hasHourlyDataAccess?: boolean;
  spotName: string | undefined;
  spotId: string | undefined;
  subregionId: string | undefined;
  sunlightTimes: SunlightTimes;
  tide: TideData;
  units: { surf: Units | null; weather: Units | null; wave: Units | null; wind: Units | null };
  surfDay: Array<SurfTimestep>;
  swellsDay: Array<SwellsTimestep>;
  weatherDay: Array<WeatherDetail>;
  weatherIconPath: string;
  windDay: Array<WindTimestep>;
  subregionName: string | undefined;
  utcOffset: number;
  showObservedData?: boolean;
  onShowObservedDataChange?: (value: boolean) => void;
  useNearshoreFromWaveEnergy?: boolean;
  waveEnergy?: WaveEnergyGraphData;
}

const TABLE_BOTTOM_PADDING_FOR_STICKY_HEADER = 70;
const STICKY_HEADER_ACTIVATION_OFFSET = 80;

const TableDayContainer: React.FC<Props> = ({
  consistencyDay,
  dayIndex,
  daySummary,
  hideProbability,
  isExpanded,
  isHourlyExpanded = false,
  isHistoricView,
  isLoading,
  observations,
  onExpandCollapse,
  onToggleHourly,
  ratingDay,
  spotName = undefined,
  spotId = undefined,
  subregionId,
  sunlightTimes,
  tide,
  units,
  surfDay,
  swellsDay,
  weatherDay,
  weatherIconPath,
  windDay,
  subregionName,
  utcOffset,
  hasHourlyDataAccess,
  showObservedData = true,
  onShowObservedDataChange,
  useNearshoreFromWaveEnergy = false,
  waveEnergy,
}) => {
  const userSettings = useAppSelector(getUserSettings);
  const tableRowsRef = useRef<HTMLDivElement>(null);
  const dayHeadersRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isTableHeaderSticky, setIsTableHeaderSticky] = useState(false);
  const [isDayHeaderSticky, setIsDayHeaderSticky] = useState(false);
  const isMobile = useMaxWidthTablet();
  const spotReportNotes: SpotReportNotes = useAppSelector(getSpotReportNotes);

  const dateFormat = userSettings?.date?.format ? userSettings.date.format : 'MDY';
  const showTide = tide?.days?.length || tide.loading || tide.error;
  const dateFormatString = dateFormat === 'MDY' ? 'M/d' : 'd/M';

  const dayHeading = useMemo(() => {
    let dayHeadingPrefix = '';
    if (dayIndex === 0 && !isHistoricView) {
      dayHeadingPrefix = 'Today, ';
    } else if (dayIndex === 1 && !isHistoricView) {
      dayHeadingPrefix = 'Tomorrow, ';
    }
    return swellsDay?.[0]?.timestamp
      ? dayHeadingPrefix +
          formatTimestampInTimezone(
            swellsDay?.[0]?.timestamp,
            swellsDay?.[0]?.utcOffset,
            dayIndex <= 1 && !isHistoricView ? dateFormatString : `EEEE, ${dateFormatString}`,
          )
      : '';
  }, [dateFormatString, dayIndex, isHistoricView, swellsDay]);

  const toggleDayExpanded = useCallback(() => {
    onExpandCollapse(dayIndex);
  }, [dayIndex, onExpandCollapse]);

  const handleToggleHourly = useCallback(() => {
    onToggleHourly?.(dayIndex);
  }, [dayIndex, onToggleHourly]);

  const dayBottomPadding = useMemo(() => {
    const summaryHeight = summaryRef.current?.clientHeight ?? 0;
    const tableRowHeight = tableRowsRef.current?.clientHeight ?? 0;
    const dayHeaderHeight = dayHeadersRef.current?.clientHeight ?? 0;
    const result = summaryHeight + tableRowHeight - dayHeaderHeight;
    return result * -1;
    // These are needed to recalculate the padding when the heights change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    summaryRef.current?.clientHeight,
    tableRowsRef.current?.clientHeight,
    dayHeadersRef.current?.clientHeight,
  ]);

  const onScrollHandler = useCallback(() => {
    const dayHeaderRef = dayHeadersRef.current;
    const tableRows = tableRowsRef.current;
    if (!dayHeaderRef || !tableRows) return;
    const { top: dayTop, bottom: dayBottom } = dayHeaderRef.getBoundingClientRect();
    const { top: tableTop, bottom: tableBottom } = tableRows.getBoundingClientRect();
    if (dayTop <= 0 && dayBottom >= dayBottomPadding) {
      setIsDayHeaderSticky(true);
    } else {
      setIsDayHeaderSticky(false);
    }
    // Only activate sticky header when table has scrolled up enough to need it
    // and there's still enough content below to justify keeping it sticky
    if (
      tableTop <= -STICKY_HEADER_ACTIVATION_OFFSET &&
      tableBottom >= TABLE_BOTTOM_PADDING_FOR_STICKY_HEADER
    ) {
      setIsTableHeaderSticky(true);
    } else {
      setIsTableHeaderSticky(false);
    }
  }, [dayBottomPadding]);

  const forecastContentDateHeading = useMemo(() => {
    if (dayIndex === 0) return 'Today';
    if (dayIndex === 1) return 'Tomorrow';
    if (daySummary?.days?.[dayIndex]?.dayToWatch) return 'Day to Watch';
    return undefined;
  }, [dayIndex, daySummary?.days]);

  const forecaster = useMemo(() => {
    const forecasterData = daySummary?.days?.[dayIndex]?.forecaster;
    if (forecasterData) {
      return {
        name: forecasterData.name,
        avatarUrl: forecasterData.avatar,
      };
    }
    return undefined;
  }, [dayIndex, daySummary?.days]);

  const forecastNote = useMemo(() => {
    if (dayIndex === 0 && isMobile && spotReportNotes) {
      const { subregion: subregionNote, spot: spotNote } = spotReportNotes;
      if (subregionNote || spotNote) {
        return `${subregionNote || ''}${subregionNote && spotNote ? '<br /><br />' : ''}${
          spotNote || ''
        }`;
      }
    }
    return undefined;
  }, [dayIndex, isMobile, spotReportNotes]);

  const showPaywall = false;

  // Show full summary if it's one of the first 5 days, OR if it's a day to watch for days after 5
  const showForecastDayContent = useMemo(() => {
    const isFirstFiveDays = dayIndex < 5;
    const isDayToWatchAfterFive = dayIndex >= 5 && daySummary?.days?.[dayIndex]?.dayToWatch;
    const shouldShowFullSummary = isFirstFiveDays || isDayToWatchAfterFive;

    // Only show if headline and observation exist
    if (shouldShowFullSummary) {
      return daySummary?.days?.[dayIndex]?.headline && daySummary?.days?.[dayIndex]?.observation;
    }

    return false;
  }, [dayIndex, daySummary?.days]);

  useEffect(() => {
    if (isExpanded) {
      getWindow()?.addEventListener('scroll', onScrollHandler);
    }
    return () => {
      getWindow()?.removeEventListener('scroll', onScrollHandler);
    };
  }, [isExpanded, onScrollHandler]);

  const handleObservedDataToggleChange = useCallback(() => {
    onShowObservedDataChange?.(!showObservedData);
  }, [onShowObservedDataChange, showObservedData]);

  /* Allows the table days to mount without blocking the render thread */
  useMount(() => setTimeout(() => setIsMounted(true), 0));

  return (
    <Box
      className={styles.dayContainerWrapper}
      data-testid={`${!isLoading ? 'forecast-table-day' : 'forecast-table-day-loading'}`}
      id={`forecast-day-${dayIndex + 1}`}
    >
      {dayIndex !== 0 && (
        <Divider className={styles.dayDivider} orientation="horizontal" flexItem />
      )}
      <Box className={styles.dayContainerContent} data-testid="forecast-table-day-content">
        <Box
          className={styles.dayHeading}
          onClick={toggleDayExpanded}
          data-testid="forecast-table-day-header"
          ref={dayHeadersRef}
        >
          <Typography variant="callout1">{dayHeading}</Typography>
          {daySummary?.days?.[dayIndex]?.dayToWatch && !isHistoricView && (
            <StarCircle className={styles.star} />
          )}
          <Disclosure rotation={isExpanded ? 0 : 180} />
        </Box>
        {
          /* When the day header is sticky we need to duplicate it to avoid
        a content shift as the sticky header changes to fixed positioning */
          isDayHeaderSticky && isMobile && isExpanded && (
            <Box
              className={classNames(styles.dayHeading, { [styles.sticky]: isDayHeaderSticky })}
              onClick={toggleDayExpanded}
              data-testid="forecast-table-day-header"
            >
              <Typography variant="callout1">{dayHeading}</Typography>
              <Disclosure rotation={isExpanded ? 0 : 180} />
            </Box>
          )
        }
        {isExpanded &&
          !isHistoricView &&
          spotId &&
          spotName &&
          subregionId &&
          subregionName &&
          showForecastDayContent && (
            <ForecastDayContent
              conditions=""
              dayIndex={dayIndex}
              forecastDate={daySummary.days?.[dayIndex].forecastDay}
              forecastNote={forecastNote}
              forecaster={forecaster}
              headline={daySummary.days?.[dayIndex].headline as string}
              isPaywallActive={showPaywall}
              lastPublished={daySummary.lastPublished}
              observation={daySummary.days?.[dayIndex].observation as string}
              spotId={spotId}
              spotName={spotName}
              subregionId={subregionId}
              subregionName={subregionName}
              title={forecastContentDateHeading}
              titleSuffix={
                daySummary?.days?.[dayIndex]?.dayToWatch ? (
                  <StarCircle className={styles.titleSuffixStar} />
                ) : undefined
              }
              type="table"
              utcOffset={utcOffset}
            />
          )}
        <Box className={styles.dayTableWrapper}>
          {isMounted ? (
            <Box ref={tableRowsRef}>
              {dayIndex === 0 && (
                <Box
                  className={classNames(styles.header, {
                    [styles.headerWithReport]: isExpanded && showForecastDayContent,
                  })}
                >
                  <ObservedDataToggle
                    defaultChecked={showObservedData}
                    onChange={handleObservedDataToggleChange}
                    spotId={spotId}
                    spotName={spotName}
                    subregionId={subregionId}
                    subregionName={subregionName}
                  />
                  {hasHourlyDataAccess && (
                    <TableHeaderHourlyToggle
                      isHourlyExpanded={isHourlyExpanded}
                      onToggleHourly={handleToggleHourly}
                      spotId={spotId}
                      spotName={spotName}
                      subregionId={subregionId}
                      subregionName={subregionName}
                    />
                  )}
                </Box>
              )}
              <ForecastTableRows
                dayIndex={dayIndex}
                hideProbability={hideProbability}
                isExpanded={isExpanded}
                isHeaderSticky={isTableHeaderSticky}
                isHistoricView={isHistoricView}
                loading={isLoading}
                observations={observations}
                ratingsHours={ratingDay}
                consistency={consistencyDay}
                toggleDayExpanded={toggleDayExpanded}
                showObservedData={showObservedData}
                surfHours={surfDay}
                surfUnits={units?.surf}
                swellsHours={swellsDay}
                useNearshoreFromWaveEnergy={useNearshoreFromWaveEnergy}
                waveEnergy={waveEnergy}
                waveUnits={units?.wave}
                weatherHours={weatherDay}
                weatherIconPath={weatherIconPath}
                weatherUnits={units?.weather}
                windHours={windDay}
                windUnits={units?.wind}
              />
            </Box>
          ) : null}

          {isExpanded && showTide && isMounted ? (
            <TimeTableContainer
              tide={tide}
              sunlightTimes={sunlightTimes}
              daysToDisplay={[dayIndex]}
              toggleDayExpanded={toggleDayExpanded}
              dayIndex={dayIndex}
              spotName={spotName}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default TableDayContainer;
